import {
  Settings2,
  LayoutDashboard,
  BarChart3,
  Search,
  Activity,
  BookOpen,
  BookText,
  Database,
  LibraryBig,
  Shield,
  Rocket,
} from 'lucide-react';
import { internalUrl, websiteUrl } from '@knitknot/shared/lib/appUrls';
import { consoleRoutes } from './routes';

export type RouteKey =
  | 'onboarding'
  | 'overview'
  | 'knowledge'
  | 'records'
  | 'demand'
  | 'benchmarks'
  | 'data'
  | 'publish'
  | 'traffic'
  | 'internal'
  | 'docs'
  | 'settings';

export interface NavItem {
  key: RouteKey;
  title: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
  isMasterPage?: boolean;
  isExternal?: boolean;
  newTab?: boolean;
  locked?: boolean;
  bottom?: boolean;
  badge?: number;
}

export const NAV_ENTRIES: NavItem[] = [
  { key: 'onboarding', title: 'Get started', href: consoleRoutes.setup, icon: Rocket },
  {
    key: 'overview',
    title: 'Overview',
    href: consoleRoutes.overview,
    icon: LayoutDashboard,
  },
  {
    key: 'knowledge',
    title: 'Facts',
    href: consoleRoutes.knowledge.root,
    icon: LibraryBig,
  },
  {
    key: 'records',
    title: 'Records',
    href: '/records',
    icon: Database,
  },
  {
    key: 'demand',
    title: 'Prompt library',
    href: consoleRoutes.demand.root,
    icon: Search,
  },
  {
    key: 'benchmarks',
    title: 'Benchmarks',
    href: consoleRoutes.benchmarks.results,
    icon: BarChart3,
  },
  { key: 'data', title: 'Intelligence', href: consoleRoutes.data.root, icon: Database },
  { key: 'publish', title: 'Content', href: consoleRoutes.publish.root, icon: BookOpen },
  { key: 'traffic', title: 'Traffic', href: consoleRoutes.traffic.root, icon: Activity },
  {
    key: 'internal',
    title: 'Internal',
    href: internalUrl(),
    icon: Shield,
    isExternal: true,
    newTab: false,
    bottom: true,
  },
  {
    key: 'docs',
    title: 'Docs',
    href: websiteUrl('/docs/'),
    icon: BookText,
    isExternal: true,
    bottom: true,
  },
  {
    key: 'settings',
    title: 'Settings',
    href: consoleRoutes.settings.root,
    icon: Settings2,
    isMasterPage: true,
    bottom: true,
  },
];

export const NAV_ITEMS: NavItem[] = NAV_ENTRIES;

const LIVE_WHILE_LOCKED: ReadonlySet<RouteKey> = new Set<RouteKey>(['onboarding', 'settings', 'docs', 'internal']);

export function getFilteredNav(
  locked = false,
  showInternal = false,
  factLedgerBetaEnabled = false,
): NavItem[] {
  const permitted = NAV_ENTRIES.filter(
    (entry) => (showInternal || entry.key !== 'internal') && (factLedgerBetaEnabled || entry.key !== 'records'),
  );
  const visible = permitted.filter((entry) => entry.key !== 'onboarding');
  if (!locked) return visible;
  return permitted.map((entry) => (!LIVE_WHILE_LOCKED.has(entry.key) ? { ...entry, locked: true } : entry));
}

/**
 * Target routes have one global owner, so selected state is strict prefix
 * matching. Legacy URLs redirect before they render and never need ownership
 * exceptions.
 */
export function isPathActive(navHref: string, currentPath: string): boolean {
  if (!navHref.startsWith('/')) return false;
  return currentPath === navHref || currentPath.startsWith(`${navHref}/`);
}

/**
 * A page record has one stable URL but can be opened from Intelligence,
 * Content, or Traffic. Preserve that task context in the rail using the
 * existing `?from=sources-tab:*` receipt; every non-record route remains a
 * strict path owner.
 */
export function navigationOwnerPath(pathname: string, search = ''): string {
  if (!pathname.startsWith(`${consoleRoutes.data.sources}/`)) return pathname;
  const origin = new URLSearchParams(search).get('from');
  const tab = origin?.match(/^sources-tab:(.+)$/)?.[1];
  if (tab === 'pages' || tab === 'coverage' || tab === 'crawlers' || tab === 'delivery') {
    return consoleRoutes.publish.root;
  }
  if (tab === 'traffic') return consoleRoutes.traffic.root;
  return consoleRoutes.data.root;
}
